// The survey as respondents see it, rendered read-through with the REAL
// SectionStep components and throwaway local state — you can try the controls,
// nothing is saved. Two consumers: the cycle page's "Preview survey" (new
// window, pinned version via the portal API) and the instrument editor's
// whole-draft preview (unpublished local draft).

import { useState, type JSX } from 'react';
import type { InstrumentDefinition } from './api';
import { renderInstrumentText } from './markup';
import { SectionStep, type ScoreMap } from './survey/SectionStep';

export function SurveyPreview({
  definition,
  context,
}: {
  definition: InstrumentDefinition;
  /** Short label for what is being previewed, e.g. "instrument 2026.1". */
  context?: string;
}): JSX.Element {
  const [scores, setScores] = useState<ScoreMap>({});
  const [texts, setTexts] = useState<Record<string, string>>({});
  // One shared state: expanding the 1-4 explanation in any section mirrors it
  // across every section on the page.
  const [scaleHelpOpen, setScaleHelpOpen] = useState(false);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="health-banner" role="status">
        <strong>Preview{context ? ` — ${context}` : ''}.</strong> This is what respondents see, with
        every section on one page. You can try the controls; nothing entered here is saved.
      </div>

      <div className="card">
        <h2 style={{ fontSize: 22, marginBottom: 6 }}>{renderInstrumentText(definition.title)}</h2>
        <p style={{ fontSize: 13.5, color: 'var(--health-grey)', marginBottom: 10 }}>
          Respondents score each statement from 1 to 4:
        </p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.8 }}>
          {['1', '2', '3', '4'].map((n) => (
            <li key={n}>
              <strong>{n}</strong> — {renderInstrumentText(definition.scale.labels[n] ?? '')}
            </li>
          ))}
        </ul>
      </div>

      {definition.sections.map((section) => (
        <SectionStep
          key={section.id}
          section={section}
          scale={definition.scale.labels}
          scores={scores}
          texts={texts}
          onScore={(id, v) => setScores((p) => ({ ...p, [id]: v ?? null }))}
          onText={(id, v) => setTexts((p) => ({ ...p, [id]: v }))}
          scaleHelpOpen={scaleHelpOpen}
          onScaleHelpToggle={setScaleHelpOpen}
        />
      ))}
    </div>
  );
}
